import { supabase } from "@/integrations/supabase/client";
import type { ReportBlock, ReportSettings } from "./block-types";

export interface ReportData {
  positions?: Array<{
    id: string;
    instrument_name: string;
    asset_class: string | null;
    market_value: number | null;
    cost_basis: number | null;
    quantity: number | null;
    currency: string | null;
    gain_loss?: number | null;
  }>;
  capitalEvents?: Array<{
    id: string;
    type: string;
    amount: number;
    date: string;
    fund_name: string | null;
  }>;
  transactions?: Array<{
    id: string;
    type: string;
    amount: number;
    date: string;
    description: string | null;
  }>;
  summary?: {
    totalNav: number;
    totalCost: number;
    totalGainLoss: number;
    returnPct: number;
    positionCount: number;
  };
  narrative?: string;
  marketCommentary?: string;
}

/** Determine which data sources are needed based on included blocks. */
function getRequiredSources(blocks: ReportBlock[]): Set<string> {
  const sources = new Set<string>();
  for (const block of blocks) {
    switch (block.type) {
      case "portfolio_overview":
      case "asset_allocation":
      case "performance_attribution":
      case "holdings_table":
      case "alternatives_detail":
      case "top_holdings":
      case "fee_summary":
        sources.add("positions");
        break;
      case "capital_activity":
        sources.add("capitalEvents");
        break;
      case "transaction_log":
        sources.add("transactions");
        break;
      case "executive_summary":
        sources.add("positions");
        sources.add("narrative");
        break;
      case "market_commentary":
        sources.add("marketCommentary");
        break;
    }
  }
  return sources;
}

/**
 * Fetch all data needed for the selected report blocks in one pass.
 * Only queries sources required by the included blocks.
 */
export async function fetchReportData(
  blocks: ReportBlock[],
  settings: ReportSettings,
  orgId: string,
  familyId?: string | null
): Promise<ReportData> {
  const sources = getRequiredSources(blocks);
  const data: ReportData = {};

  const promises: Promise<void>[] = [];

  if (sources.has("positions")) {
    promises.push(
      (async () => {
        // Join positions → instruments (name, type, currency) and latest position_metrics (mv, cost, units)
        let query = supabase
          .from("position_metrics")
          .select(`
            id,
            mv_base,
            cost_base,
            units,
            period_end,
            position:position_id (
              id,
              instrument:instrument_id (
                name,
                type,
                currency
              )
            )
          `)
          .eq("org_id", orgId)
          .order("period_end", { ascending: false });

        if (familyId) query = query.eq("family_id", familyId);

        const { data: metrics, error } = await query;
        if (error) throw error;

        // Deduplicate: keep only the latest metric per position
        const seenPositions = new Set<string>();
        const latestMetrics = (metrics || []).filter((m) => {
          const posId = (m.position as any)?.id;
          if (!posId || seenPositions.has(posId)) return false;
          seenPositions.add(posId);
          return true;
        });

        data.positions = latestMetrics.map((m) => {
          const pos = m.position as any;
          const inst = pos?.instrument as any;
          const mv = m.mv_base ?? 0;
          const cost = m.cost_base ?? 0;
          return {
            id: pos?.id || m.id,
            instrument_name: inst?.name || "Unknown",
            asset_class: inst?.type || null,
            market_value: mv,
            cost_basis: cost,
            quantity: m.units,
            currency: inst?.currency || null,
            gain_loss: mv - cost,
          };
        });

        // Compute summary
        const totalNav = data.positions.reduce((s, p) => s + (p.market_value || 0), 0);
        const totalCost = data.positions.reduce((s, p) => s + (p.cost_basis || 0), 0);
        data.summary = {
          totalNav,
          totalCost,
          totalGainLoss: totalNav - totalCost,
          returnPct: totalCost > 0 ? ((totalNav - totalCost) / totalCost) * 100 : 0,
          positionCount: data.positions.length,
        };
      })()
    );
  }

  if (sources.has("capitalEvents")) {
    promises.push(
      (async () => {
        let query = supabase
          .from("capital_events")
          .select("id, kind, amount, due_at, fund_name")
          .eq("org_id", orgId)
          .order("due_at", { ascending: false });

        if (familyId) query = query.eq("family_id", familyId);
        if (settings.periodStart) query = query.gte("due_at", settings.periodStart);
        if (settings.periodEnd) query = query.lte("due_at", settings.periodEnd);

        const { data: events, error } = await query;
        if (error) throw error;
        data.capitalEvents = (events || []).map((e) => ({
          id: e.id,
          type: e.kind || "unknown",
          amount: e.amount || 0,
          date: e.due_at || "",
          fund_name: e.fund_name,
        }));
      })()
    );
  }

  if (sources.has("transactions")) {
    promises.push(
      (async () => {
        let query = supabase
          .from("transactions")
          .select("id, class, amount, date, description")
          .eq("org_id", orgId)
          .order("date", { ascending: false });

        if (familyId) query = query.eq("family_id", familyId);
        if (settings.periodStart) query = query.gte("date", settings.periodStart);
        if (settings.periodEnd) query = query.lte("date", settings.periodEnd);

        const limit = blocks.find((b) => b.type === "transaction_log")?.config?.limit as number || 50;
        query = query.limit(limit);

        const { data: txns, error } = await query;
        if (error) throw error;
        data.transactions = (txns || []).map((t) => ({
          id: t.id,
          type: t.class || "unknown",
          amount: t.amount,
          date: t.date,
          description: t.description,
        }));
      })()
    );
  }

  // AI content is fetched separately via edge functions — just mark as undefined for now
  if (sources.has("narrative")) {
    data.narrative = undefined;
  }
  if (sources.has("marketCommentary")) {
    data.marketCommentary = undefined;
  }

  await Promise.all(promises);
  return data;
}
